import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from "../../api";
import styled from 'styled-components';
import ProductCard from '../product/ProductCard';

/* ================= UI ================= */
const Container = styled.div`
    max-width: 1300px;
    margin: 0 auto;
    padding: 32px 20px;
`;

const ContentLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 28px;
    align-items: start;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const AisleBar = styled.div`
    position: sticky;
    top: 24px;
    height: calc(100vh - 48px);
    overflow-y: auto;

    background: white;
    border: 1px solid #eee;
    border-radius: 18px;
    padding: 14px;

    display: flex;
    flex-direction: column;
    gap: 8px;

    box-shadow: 0 10px 28px rgba(0,0,0,0.04);

    overscroll-behavior: contain;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 999px;
    }

    @media (max-width: 800px) {
        position: static;
        height: auto;
        max-height: none;
        overflow-x: auto;
        overflow-y: hidden;
        flex-direction: row;
    }
`;

const AisleTitle = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #666;
    margin-bottom: 6px;

    @media (max-width: 800px) {
        display: none;
    }
`;

const AisleTab = styled.button`
    padding: 10px 14px;
    border-radius: 12px;
    border: 1px solid ${props => (props.$active ? '#22c55e' : '#e5e7eb')};
    background: ${props => (props.$active ? '#ecfdf5' : 'white')};
    color: ${props => (props.$active ? '#15803d' : '#333')};
    font-weight: ${props => (props.$active ? 700 : 500)};
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;

    &:hover {
        border-color: #22c55e;
        background: #f0fdf4;
    }

    @media (max-width: 800px) {
        white-space: nowrap;
    }
`;

const ProductsArea = styled.div`
    min-width: 0;
`;

const Header = styled.div`
    margin-bottom: 24px;
`;

const StoreInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const Logo = styled.img`
    width: 60px;
    height: 60px;
    border-radius: 12px;
    object-fit: contain;
    background: white;
    padding: 6px;
    border: 1px solid #eee;
`;

const StoreText = styled.div``;

const StoreTitle = styled.h1`
    font-size: 28px;
    margin: 0;
`;

const Subtitle = styled.p`
    color: #666;
    margin-top: 4px;
    font-size: 14px;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
`;

const Center = styled.div`
    text-align: center;
    padding: 60px 20px;
    color: #666;
`;

const Loading = styled.div`
    text-align: center;
    padding: 40px;
    color: #666;
`;

/* ================= PAGE ================= */

const StorePage = () => {
    const { storeId } = useParams();

    const [store, setStore] = useState(null);
    const [products, setProducts] = useState([]);

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const limit = 40;
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {

        setSelectedCategory('All');

        setProducts([]);

        setPage(1);

        setHasMore(true);

        axios.get(`/api/stores/${storeId}`)

            .then(res => setStore(res.data))

            .catch(console.error);

        axios.get(`/api/products/store/${storeId}/categories`)

            .then(res => setCategories(res.data))

            .catch(console.error);

    }, [storeId]);

    useEffect(() => {
        setProducts([]);
        setPage(1);
        setHasMore(true);
        fetchProducts(1, true);
    }, [storeId, selectedCategory]);



    const fetchProducts = async (pageToLoad, reset = false) => {
        try {
            if (reset) setLoading(true);
            else setLoadingMore(true);

            const params = {
                page: pageToLoad,
                limit: 40
            };

            if (selectedCategory !== 'All') {
                params.category = selectedCategory;
            }

            const res = await axios.get(`/api/products/store/${storeId}`, { params });

            const newProducts = res.data.products || [];

            setProducts(prev =>
                reset ? newProducts : [...prev, ...newProducts]
            );

            setHasMore(res.data.hasMore);
            setPage(pageToLoad);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (loadingMore || !hasMore) return;

            const nearBottom =
                window.innerHeight + window.scrollY >= document.body.offsetHeight - 600;

            if (nearBottom) {
                fetchProducts(page + 1);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [page, hasMore, loadingMore, selectedCategory, storeId]);

    if (loading) return <Loading>Loading store...</Loading>;
    if (!store) return <Center>Store not found</Center>;

    return (
        <Container>

            <ContentLayout>

                <AisleBar>
                    <AisleTitle>Aisles</AisleTitle>

                    <AisleTab
                        $active={selectedCategory === 'All'}
                        onClick={() => setSelectedCategory('All')}
                    >
                        All
                    </AisleTab>

                    {categories.map(category => (
                        <AisleTab
                            key={category}
                            $active={selectedCategory === category}
                            onClick={() => setSelectedCategory(category)}
                        >
                            {category}
                        </AisleTab>
                    ))}
                </AisleBar>

                <ProductsArea>

                    <Header>

                        <StoreInfo>

                            <Logo src={store.logo} alt={store.name} />

                            <StoreText>

                                <StoreTitle>{store.name}</StoreTitle>

                                <Subtitle>{store.description}</Subtitle>

                            </StoreText>

                        </StoreInfo>

                    </Header>

                    <SectionHeader>

                        <h2>{selectedCategory}</h2>

                        <span>{products.length} loaded</span>

                    </SectionHeader>

                    {products.length === 0 ? (

                        <Center>No products in this aisle</Center>

                    ) : (

                        <Grid>

                            {products.map(product => (

                                <ProductCard

                                    key={product._id}

                                    product={product}

                                    onAddToCart={() => console.log('Add:', product)}

                                />

                            ))}

                        </Grid>

                    )}

                    {loadingMore && <Loading>Loading more products...</Loading>}

                    {!hasMore && products.length > 0 && <Center>End of results</Center>}

                </ProductsArea>

            </ContentLayout>

        </Container>
    );
};

export default StorePage;